import { NextRouter, withRouter } from "next/router";
import React from "react";
import classNames from "classnames";

import {
  HOME,
  ABOUT_US,
  USER_ZONE,
  REGULATIONS,
  CHANGE_PASSWORD,
  PAYMENT,
  SIGN_UP_SUCCESS,
  SIGN_IN,
  FORGOT_PASSWORD,
  RESET_PASSWORD,
  SIGN_UP,
  PROFILE_SETTINGS,
  EDUCATION,
  SUPPLY,
  SEARCH_MASSAGE,
  PROFILE_DETAILS,
  EDIT_ANNOUNCEMENT,
  POST_NEW_PARTNER_PROFILE_SUCCESS,
  DUPLICATE_ANNOUNCEMENT,
  ACTIVATE_ANNOUNCEMENT,
  FIX_ANNOUNCEMENT,
  EXTEND_ANNOUNCEMENT,
  CHANGE_EMAIL,
  CHANGE_EMAIL_CONFIRM_CURRENT,
  CHANGE_EMAIL_CONFIRM_NEW,
  EXTEND_ANNOUNCEMENT_WAIT,
  POST_NEW_ANNOUNCEMENT_WAIT,
  POST_NEW_PARTNER_PROFILE,
  MOBILE_APP,
  SEARCH_PARTNER_PROFILE,
  BOOKING_PARTNER_PROFILE,
  EDIT_PARTNER_PROFILE,
  EDIT_PARTNER_PROFILE_SUCCESS,
} from "constants/routes";
import {
  BackButton,
  CloseButton,
  HamburgerButton,
} from "components/common/Button/Button";
import classes from "./NavBar.module.css";
import NotificationContext, {
  NotificationContextType,
} from "context/notification/notification-context";
import NavContent from "./NavContent/NavContent";
import { Logo, LogoWithoutText } from "components/common/Logo/Logo";
import MobilePageHeading from "./NavContent/MobilePageHeading/MobilePageHeading";
import {
  HomeNavLinks,
  MassageMasterLoggedInNavLinks,
  MassageMasterZoneNavLinks,
  SearchMassageNavLinks,
} from "./NavContent/NavLinks/NavLinks";
import { SUMMARY_TAB, CALENDAR_TAB } from "constants/announcementTypes";
import Filters from "./Filters/Filters";
import { Option } from "utils/interfaces/commonInterfacesAndTypes";
import { decodeParams, encodeValues } from "utils/functions/encodeDecodeURI";
import { massageSubcategories } from "constants/massageSubcategories";
import withContext from "hoc/WithContext";
import AuthContext, { AuthContextType } from "context/auth/auth-context";
import CustomModal from "components/common/CustomModal/CustomModal";
import withTranslation from "next-translate/withTranslation";
import { Place } from "utils/interfaces/announcementForm";
import PlaceContext, { PlaceContextType } from "context/place/place-context";

const pagesWithGreenNavbar: string[] = [SIGN_UP_SUCCESS, USER_ZONE];

const announcementsTabs = new Map([
  [SUMMARY_TAB, "profile_summary"],
  [CALENDAR_TAB, "calendar_tab"],
]);

type NavBarState = {
  selectedPlace: Place | null;
  selectedCategory: Option | null;
  selectedCategories: {
    [key: string]: string[] | { [key: string]: string[] };
  };
  navExpanded: boolean;
  navMoved: boolean;
  removeAccountModalVisible: boolean;
};

type NavBarProps = {
  router: NextRouter;
  auth: AuthContextType;
  notification: NotificationContextType;
  placeContext: PlaceContextType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  i18n: any;
};

class NavBar extends React.Component<NavBarProps, NavBarState> {
  constructor(props: NavBarProps) {
    super(props);
    this.state = {
      selectedPlace: {},
      selectedCategory: null,
      selectedCategories: {
        mainCategories: [],
        subCategories: {},
      },
      navExpanded: false,
      navMoved: false,
      removeAccountModalVisible: false,
    };
  }

  componentDidMount() {
    window.addEventListener("scroll", this.windowScrollHandler);
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.windowScrollHandler);
  }

  componentDidUpdate(prevProps: NavBarProps) {
    const {
      pathname: newPathname,
      query: newQuery,
      asPath: newFullPath,
    } = this.props.router;
    const {
      pathname: oldPathname,
      query: oldQuery,
      asPath: oldFullPath,
    } = prevProps.router;

    if (
      newPathname !== oldPathname ||
      newQuery.tab !== oldQuery.tab ||
      newFullPath !== oldFullPath
    ) {
      this.setState({
        navExpanded: false,
        selectedCategory: null,
        selectedCategories: {
          mainCategories: [],
          subCategories: {},
        },
      });
    }

    if (
      newQuery.placeId !== oldQuery.placeId ||
      newQuery.category !== oldQuery.category ||
      newQuery !== oldQuery
    ) {
      this.props.placeContext.changePlace(
        decodeParams(newQuery.placeId || "")[0]
      );
      newQuery.category &&
        this.extractCategoriesValuesFromURL(newQuery.category);
    }

    if (prevProps.placeContext.place !== this.props.placeContext.place) {
      this.setState({ selectedPlace: { ...this.props.placeContext.place } });
    }
  }

  searchMassageHandler = () => {
    const filterValues = {
      placeId: this.state.selectedPlace?.placeId || "",
      ...(this.state.selectedCategory && {
        category: this.state.selectedCategory.value,
      }),
    };
    const encodedFilterValues = encodeValues(filterValues);
    this.props.router.push(
      {
        pathname: SEARCH_MASSAGE,
        query: {
          ...this.props.router.query,
          ...encodedFilterValues,
        },
      },
      undefined,
      { shallow: this.props.router.pathname === SEARCH_MASSAGE }
    );

    this.setState({
      selectedPlace: {
        placeId: decodeParams(this.props.router.query.placeId || "")[0],
      },
    });
  };

  applyCategoriesFilterHandler = () => {
    if (this.state.selectedCategories.mainCategories) {
      const filterValues = {
        category: this.state.selectedCategories.mainCategories as string[],
        ...(this.state.selectedCategories.subCategories as {
          [key: string]: string[];
        }),
      };
      const encodedFilterValues = encodeValues(filterValues);
      this.props.router.push(
        {
          pathname: this.props.router.pathname,
          query: {
            ...this.props.router.query,
            ...encodedFilterValues,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  searchByPlace = () => {
    const encodedPlaceId = encodeURI(this.state.selectedPlace?.placeId || "");

    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          placeId: encodedPlaceId,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  extractCategoriesValuesFromURL = (decodecategory: string[] | string) => {
    const mainCategories: string[] = [];
    const subCategories: { [key: string]: string | string[] } = {};
    const { query } = this.props.router;

    if (decodecategory instanceof Array) {
      decodecategory.forEach((category) => {
        if (query[category]) {
          const subCategoriesValues = decodeParams(
            query[category] as string[] | string
          );
          mainCategories.push(decodeURI(category));
          subCategories[decodeURI(category)] = subCategoriesValues;
        } else {
          mainCategories.push(decodeURI(category));
        }
      });
    } else if (query[query.category as string]) {
      const subCategoriesValues = decodeParams(
        query[query.category as string] as string[] | string
      );
      mainCategories.push(decodeURI(query.category as string));
      // @ts-ignore
      subCategories[decodeURI(query.category)] = subCategoriesValues;
    } else {
      const decodedCategory = decodeURI(query.category as string);
      mainCategories.push(decodedCategory);
      massageSubcategories[decodedCategory] &&
        (subCategories[decodedCategory] =
          massageSubcategories[decodedCategory]);
    }
    // @ts-ignore
    this.setState({ selectedCategories: { mainCategories, subCategories } });
  };

  selectPlaceHandler = (selectedPlace: Place) => {
    this.setState({ selectedPlace: selectedPlace });
  };

  categoryMainFilterChangeHandler = (category: string) => {
    const currentMainCategories = [
      ...(this.state.selectedCategories.mainCategories as string[]),
    ];
    const currentSubCategories = this.state.selectedCategories
      .subCategories as {
      [key: string]: string[];
    };

    if (!currentMainCategories.includes(category)) {
      currentMainCategories.push(category);
      massageSubcategories[category] &&
        (currentSubCategories[category] = massageSubcategories[category]);
    } else {
      currentMainCategories.splice(currentMainCategories.indexOf(category), 1);
      massageSubcategories[category] && (currentSubCategories[category] = []);
    }

    this.setState({
      selectedCategories: {
        mainCategories: currentMainCategories,
        subCategories: currentSubCategories,
      },
    });
  };

  categorySubFilterChangeHandler = (
    mainCategory: string,
    subCategory: string
  ) => {
    const currentMainCategories = [
      ...(this.state.selectedCategories.mainCategories as string[]),
    ];
    const currentSubCategories = this.state.selectedCategories
      .subCategories as {
      [key: string]: string[];
    };

    const currentValues = currentSubCategories[mainCategory]
      ? [...currentSubCategories[mainCategory]]
      : [];

    if (!currentValues.includes(subCategory)) {
      currentValues.push(subCategory);
      !currentMainCategories.includes(mainCategory) &&
        currentMainCategories.push(mainCategory);
    } else {
      currentValues.splice(currentValues.indexOf(subCategory), 1);

      currentValues.length === 0 &&
        currentMainCategories.splice(
          currentMainCategories.indexOf(mainCategory),
          1
        );
    }

    this.setState({
      selectedCategories: {
        mainCategories: currentMainCategories,
        subCategories: {
          ...currentSubCategories,
          [mainCategory]: currentValues,
        },
      },
    });
  };

  clearMainCategoriesFitlerHandler = () => {
    const clearedSubCategories = {
      ...(this.state.selectedCategories.subCategories as {
        [key: string]: string[];
      }),
    };
    Object.keys(clearedSubCategories).forEach((key) => {
      clearedSubCategories[key] = [];
    });

    const emptyCategoriesFilter = {
      mainCategories: [],
      subCategories: clearedSubCategories,
    };
    this.setState({ selectedCategories: emptyCategoriesFilter });

    const filterValues = {
      category: emptyCategoriesFilter.mainCategories,
      ...emptyCategoriesFilter.subCategories,
    };
    const encodedFilterValues = encodeValues(filterValues);
    this.props.router.push(
      {
        pathname: this.props.router.pathname,
        query: {
          ...this.props.router.query,
          ...encodedFilterValues,
        },
      },
      undefined,
      { shallow: true }
    );
  };

  clearSubCategoriesFilter = (mainCategory: string) => {
    if (
      (
        this.state.selectedCategories.subCategories as {
          [key: string]: string[];
        }
      )[mainCategory]
    ) {
      const clearedSubCategories = {
        ...(this.state.selectedCategories.subCategories as {
          [key: string]: string[];
        }),
        [mainCategory]: [],
      };
      const clearedMainCategories = [
        ...(this.state.selectedCategories.mainCategories as string[]),
      ];
      clearedMainCategories.splice(
        clearedMainCategories.indexOf(mainCategory),
        1
      );

      this.setState({
        selectedCategories: {
          mainCategories: clearedMainCategories,
          subCategories: clearedSubCategories,
        },
      });

      const encodedFilterValues = encodeValues({
        category: clearedMainCategories,
        ...clearedSubCategories,
      });

      this.props.router.push(
        {
          pathname: this.props.router.pathname,
          query: {
            ...this.props.router.query,
            ...encodedFilterValues,
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  clearPlaceHandler = () => {
    this.setState({ selectedPlace: null });
  };

  removeAccountHandler = () => {
    this.props.auth
      .removeAccount()
      .then(() => {
        this.props.notification.display({
          text: "missing:account_deleted",
          duration: 4000,
        });
        this.props.router.replace(HOME);
      })
      .catch(() =>
        this.props.notification.display({
          text: "missing:error_while_deleting_the_account",
          duration: 4000,
          isSuccess: false,
        })
      );
  };

  logoutHandler = () => {
    this.props.auth
      .logout()
      .then(() => this.props.router.replace(USER_ZONE))
      .catch(() =>
        this.props.notification.display({
          text: "missing:error_while_logging_out",
          duration: 3000,
          isSuccess: false,
        })
      );
  };

  goBackHandler = () => this.props.router.back();
  redirect = (path: string) => this.props.router.push(path);

  createRedirectHandler = (redirectToAnnouncements: boolean) => {
    return redirectToAnnouncements
      ? () => this.redirect(PROFILE_SETTINGS)
      : () => this.redirect(USER_ZONE);
  };

  clickHamburgerButton = () =>
    this.setState({ navExpanded: !this.state.navExpanded });

  selectCategoryHandler = (selectedCategory: Option) =>
    this.setState({ selectedCategory });

  windowScrollHandler = () => this.setState({ navMoved: window.scrollY >= 1 });

  openRemoveAccountModalHandler = () =>
    this.setState({ removeAccountModalVisible: true });
  closeRemoveAccountModalHandler = () =>
    this.setState({ removeAccountModalVisible: false });

  render() {
    const { pathname, query } = this.props.router;
    const {
      navExpanded,
      navMoved,
      selectedCategory,
      selectedCategories,
      removeAccountModalVisible,
    } = this.state;
    let isSearchMassagePage;
    let headerContent;
    const selectedPlace = this.state.selectedPlace;
    const { t } = this.props.i18n;

    if (pathname === HOME) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleCategorySelect={this.selectCategoryHandler}
              handleClearPlace={this.clearPlaceHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname.includes(SEARCH_PARTNER_PROFILE)) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleCategorySelect={this.selectCategoryHandler}
              handleClearPlace={this.clearPlaceHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname.includes(BOOKING_PARTNER_PROFILE)) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleCategorySelect={this.selectCategoryHandler}
              handleClearPlace={this.clearPlaceHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === MOBILE_APP) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleCategorySelect={this.selectCategoryHandler}
              handleClearPlace={this.clearPlaceHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === ABOUT_US) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleClearPlace={this.clearPlaceHandler}
              handleCategorySelect={this.selectCategoryHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === USER_ZONE) {
      headerContent = (
        <NavContent
          content={<MassageMasterZoneNavLinks expanded={navExpanded} />}
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === EDUCATION) {
      headerContent = (
        <NavContent
          content={<MassageMasterZoneNavLinks expanded={navExpanded} />}
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === SUPPLY) {
      headerContent = (
        <NavContent
          content={<MassageMasterZoneNavLinks expanded={navExpanded} />}
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (
      pathname === POST_NEW_PARTNER_PROFILE ||
      pathname === POST_NEW_PARTNER_PROFILE_SUCCESS ||
      pathname === POST_NEW_ANNOUNCEMENT_WAIT ||
      pathname === EDIT_PARTNER_PROFILE ||
      pathname === EDIT_PARTNER_PROFILE_SUCCESS
    ) {
      headerContent = (
        <NavContent
          content={<MassageMasterZoneNavLinks expanded={navExpanded} />}
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === CHANGE_PASSWORD) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileLeft={<BackButton onClick={this.goBackHandler} />}
          mobileCenter={<MobilePageHeading text="change_password" />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === CHANGE_EMAIL) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileLeft={<BackButton onClick={this.goBackHandler} />}
          mobileCenter={<MobilePageHeading text="change_password" />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === PAYMENT) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileLeft={<BackButton onClick={this.goBackHandler} />}
          mobileCenter={<MobilePageHeading text="invoice_data" />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === SIGN_UP_SUCCESS) {
      headerContent = <NavContent mobileCenter={<LogoWithoutText />} />;
    } else if (pathname === SIGN_IN) {
      headerContent = (
        <NavContent
          mobileLeft={
            <BackButton onClick={this.createRedirectHandler(false)} />
          }
          mobileCenter={<MobilePageHeading text="missing:log_in" />}
        />
      );
    } else if (pathname === FORGOT_PASSWORD) {
      headerContent = (
        <NavContent
          mobileLeft={<BackButton onClick={this.goBackHandler} />}
          mobileCenter={<MobilePageHeading text="forgot_password" />}
        />
      );
    } else if (pathname === RESET_PASSWORD) {
      headerContent = <NavContent mobileCenter={<LogoWithoutText />} />;
    } else if (pathname === SIGN_UP) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterZoneNavLinks
              visibleReturnMainPageBtn
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === PROFILE_SETTINGS) {
      let mobilePageHeadingText: string | undefined;

      if (query.tab) {
        mobilePageHeadingText = announcementsTabs.get(query.tab as string);
      }

      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileLeft={
            query.tab ? (
              <BackButton onClick={this.createRedirectHandler(true)} />
            ) : (
              <MobilePageHeading
                text="nice_to_see_you"
                className={classNames({ [classes.hidden]: navExpanded })}
              />
            )
          }
          mobileCenter={
            mobilePageHeadingText ? (
              <MobilePageHeading text={mobilePageHeadingText} />
            ) : (
              <></>
            )
          }
          mobileRight={
            !query.tab ? (
              <HamburgerButton
                onClick={this.clickHamburgerButton}
                openedMenu={navExpanded}
              />
            ) : (
              <></>
            )
          }
        />
      );
    } else if (pathname === REGULATIONS) {
      headerContent = (
        <NavContent
          content={
            this.props.auth.user ? (
              <MassageMasterLoggedInNavLinks
                expanded={navExpanded}
                onLogoutHandler={this.logoutHandler}
                openRemoveAccountModalHandler={
                  this.openRemoveAccountModalHandler
                }
              />
            ) : (
              <HomeNavLinks
                handleSearchMassage={this.searchMassageHandler}
                handlePlaceChange={this.selectPlaceHandler}
                place={selectedPlace}
                selectedCategory={selectedCategory}
                handleCategorySelect={this.selectCategoryHandler}
                handleClearPlace={this.clearPlaceHandler}
                expanded={navExpanded}
              />
            )
          }
          mobileLeft={<BackButton onClick={this.goBackHandler} />}
          mobileCenter={
            <MobilePageHeading text="terms_of_use_and_privacy_policy" />
          }
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname.includes(PROFILE_DETAILS)) {
      headerContent = <NavContent mobileCenter={<LogoWithoutText />} />;
    } else if (pathname === SEARCH_MASSAGE) {
      headerContent = (
        <>
          <NavContent
            content={
              <SearchMassageNavLinks
                onApplyCategoriesFilterHandler={
                  this.applyCategoriesFilterHandler
                }
                handlePlaceChange={this.selectPlaceHandler}
                place={selectedPlace}
                selectedCategories={selectedCategories}
                onChangeCategoryMainFilterHandler={
                  this.categoryMainFilterChangeHandler
                }
                onChangeCategorySubFilterHandler={
                  this.categorySubFilterChangeHandler
                }
                handleSearchMassageByPlace={this.searchByPlace}
                selectedCategory={selectedCategory}
                handleCategorySelect={this.selectCategoryHandler}
                handleSearchMassageByPlaceAndCategory={
                  this.searchMassageHandler
                }
                onClearMainCategoriesHandler={
                  this.clearMainCategoriesFitlerHandler
                }
                onClearSubCategoriesHandler={this.clearSubCategoriesFilter}
                handleClearPlace={this.clearPlaceHandler}
                expanded={navExpanded}
              />
            }
            mobileLeft={<Logo />}
            mobileRight={
              <HamburgerButton
                onClick={this.clickHamburgerButton}
                openedMenu={navExpanded}
              />
            }
          />
          <Filters moved={navMoved} expanded={navExpanded} />
        </>
      );
      isSearchMassagePage = true;
    } else if (pathname.includes(EDIT_ANNOUNCEMENT)) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileCenter={<MobilePageHeading text="edit_ad" />}
          mobileRight={
            <CloseButton onClick={this.createRedirectHandler(true)} />
          }
        />
      );
    } else if (pathname.includes(DUPLICATE_ANNOUNCEMENT)) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileCenter={<MobilePageHeading text="duplicate_ad" />}
          mobileRight={
            <CloseButton onClick={this.createRedirectHandler(true)} />
          }
        />
      );
    } else if (pathname.includes(ACTIVATE_ANNOUNCEMENT)) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileCenter={<MobilePageHeading text="missing:activate_announce" />}
          mobileRight={
            <CloseButton onClick={this.createRedirectHandler(true)} />
          }
        />
      );
    } else if (pathname.includes(FIX_ANNOUNCEMENT)) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileCenter={<MobilePageHeading text="missing:edit_the_announce" />}
          mobileRight={
            <CloseButton onClick={this.createRedirectHandler(true)} />
          }
        />
      );
    } else if (
      pathname.includes(EXTEND_ANNOUNCEMENT) ||
      pathname === EXTEND_ANNOUNCEMENT_WAIT
    ) {
      headerContent = (
        <NavContent
          content={
            <MassageMasterLoggedInNavLinks
              expanded={navExpanded}
              onLogoutHandler={this.logoutHandler}
              openRemoveAccountModalHandler={this.openRemoveAccountModalHandler}
            />
          }
          mobileCenter={<MobilePageHeading text="extend_advertisement" />}
          mobileRight={
            <CloseButton onClick={this.createRedirectHandler(true)} />
          }
        />
      );
    } else if (pathname === CHANGE_EMAIL_CONFIRM_CURRENT) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleClearPlace={this.clearPlaceHandler}
              handleCategorySelect={this.selectCategoryHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else if (pathname === CHANGE_EMAIL_CONFIRM_NEW) {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleClearPlace={this.clearPlaceHandler}
              handleCategorySelect={this.selectCategoryHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    } else {
      headerContent = (
        <NavContent
          content={
            <HomeNavLinks
              handleSearchMassage={this.searchMassageHandler}
              handlePlaceChange={this.selectPlaceHandler}
              place={selectedPlace}
              selectedCategory={selectedCategory}
              handleCategorySelect={this.selectCategoryHandler}
              handleClearPlace={this.clearPlaceHandler}
              expanded={navExpanded}
            />
          }
          mobileLeft={<Logo />}
          mobileRight={
            <HamburgerButton
              onClick={this.clickHamburgerButton}
              openedMenu={navExpanded}
            />
          }
        />
      );
    }

    const greenNavColor = !navMoved && pagesWithGreenNavbar.includes(pathname);

    return (
      <>
        <header
          className={classNames(classes.navbar, {
            [classes.green]: greenNavColor,
            [classes.moved]: navMoved,
            [classes.expanded]: navExpanded,
            [classes.searchMassageNavBar]: isSearchMassagePage,
          })}
        >
          {headerContent}
        </header>
        <CustomModal
          title={t("common:delete_account_question")}
          description={t("common:delete_account_description")}
          firstBtnContent={t("common:keep_account")}
          secondBtnContent={t("common:delete_account")}
          clickFirstBtnHandler={this.closeRemoveAccountModalHandler}
          clickSecondBtnHandler={this.removeAccountHandler}
          secondBtnClassName={classes.removeAccountBtn}
          opened={removeAccountModalVisible}
          closeHandler={this.closeRemoveAccountModalHandler}
        />
      </>
    );
  }
}

export default withContext(
  withContext(
    withContext(
      withRouter(withTranslation(NavBar)),
      NotificationContext,
      "notification"
    ),
    PlaceContext,
    "placeContext"
  ),
  AuthContext,
  "auth"
);
